import { useState } from "react";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../firebase/config";
import { logUsageEvent } from "../utils/usageAnalytics";

const initialForm = {
  type: "bug",
  priority: "medium",
  classContext: "",
  contactEmail: "",
  screenshotUrl: "",
  description: ""
};

export default function Feedback() {
  const [form, setForm] = useState({
    ...initialForm,
    contactEmail: auth.currentUser?.email || ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState("");
  const [errorDetails, setErrorDetails] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.description.trim()) {
      setStatus("Please add a description.");
      return;
    }

    setSubmitting(true);
    setStatus("");
    setErrorDetails("");

    try {
      await addDoc(collection(db, "feedback"), {
        type: form.type,
        priority: form.priority,
        classContext: form.classContext || "",
        contactEmail: form.contactEmail || "",
        screenshotUrl: form.screenshotUrl || "",
        description: form.description.trim(),
        status: "new",
        userId: auth.currentUser?.uid || null,
        createdAt: serverTimestamp()
      });

      logUsageEvent({
        type: "feedback_submit",
        name: form.type,
        meta: {
          priority: form.priority,
          hasScreenshot: Boolean(form.screenshotUrl),
          classContext: form.classContext || "",
        },
      });

      setForm({
        ...initialForm,
        contactEmail: auth.currentUser?.email || ""
      });
      setStatus("Thanks! Your feedback has been submitted.");
    } catch (err) {
      console.error(err);
      setStatus("Failed to submit feedback.");
      setErrorDetails(err?.code ? `${err.code}: ${err.message}` : "Unknown error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ padding: "1.5rem" }}>
      <h1>Feedback & Issues</h1>
      <p style={{ maxWidth: "720px" }}>
        Share bugs, ideas, or questions. The more detail you can give, the easier it is to fix.
      </p>

      <form onSubmit={handleSubmit} style={{ maxWidth: "720px" }}>
        <div style={{ marginBottom: "1rem" }}>
          <label style={{ display: "block", fontWeight: 600 }}>Type</label>
          <select name="type" value={form.type} onChange={handleChange} style={inputStyles.select}>
            <option value="bug">Bug</option>
            <option value="idea">Idea</option>
            <option value="question">Question</option>
          </select>
        </div>

        <div style={{ marginBottom: "1rem" }}>
          <label style={{ display: "block", fontWeight: 600 }}>Priority</label>
          <select name="priority" value={form.priority} onChange={handleChange} style={inputStyles.select}>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
        </div>

        <div style={{ marginBottom: "1rem" }}>
          <label style={{ display: "block", fontWeight: 600 }}>Class context (optional)</label>
          <input
            type="text"
            name="classContext"
            value={form.classContext}
            onChange={handleChange}
            placeholder="e.g. Year 9 Science"
            style={{ ...inputStyles.input, width: "100%" }}
          />
        </div>

        <div style={{ marginBottom: "1rem" }}>
          <label style={{ display: "block", fontWeight: 600 }}>Contact email (optional)</label>
          <input
            type="email"
            name="contactEmail"
            value={form.contactEmail}
            onChange={handleChange}
            placeholder="name@school.org"
            style={{ ...inputStyles.input, width: "100%" }}
          />
        </div>

        <div style={{ marginBottom: "1rem" }}>
          <label style={{ display: "block", fontWeight: 600 }}>Screenshot URL (optional)</label>
          <input
            type="url"
            name="screenshotUrl"
            value={form.screenshotUrl}
            onChange={handleChange}
            placeholder="https://..."
            style={{ ...inputStyles.input, width: "100%" }}
          />
        </div>

        <div style={{ marginBottom: "1rem" }}>
          <label style={{ display: "block", fontWeight: 600 }}>Description</label>
          <textarea
            name="description"
            value={form.description}
            onChange={handleChange}
            rows={6}
            style={{ ...inputStyles.textarea, width: "100%" }}
          />
        </div>

        <button
          type="submit"
          disabled={submitting}
          style={submitting ? { ...buttonStyles.primary, ...buttonStyles.disabled } : buttonStyles.primary}
        >
          {submitting ? "Submitting..." : "Submit feedback"}
        </button>

        {status && (
          <p style={{ marginTop: "0.75rem" }}>{status}</p>
        )}
        {errorDetails && (
          <p style={{ marginTop: "0.5rem", color: "#b00020" }}>
            {errorDetails}
          </p>
        )}
      </form>
    </div>
  );
}

const buttonStyles = {
  primary: {
    background: "#4a90e2",
    color: "white",
    border: "none",
    padding: "0.65rem 1rem",
    borderRadius: "8px",
    fontWeight: 600,
    cursor: "pointer",
  },
  disabled: {
    opacity: 0.6,
    cursor: "not-allowed",
  },
};

const inputStyles = {
  input: {
    padding: "0.65rem 0.75rem",
    borderRadius: "6px",
    border: "1px solid #cfd7e3",
    fontSize: "1rem",
  },
  select: {
    padding: "0.6rem 0.75rem",
    borderRadius: "6px",
    border: "1px solid #cfd7e3",
    fontSize: "1rem",
    background: "white",
  },
  textarea: {
    padding: "0.65rem 0.75rem",
    borderRadius: "6px",
    border: "1px solid #cfd7e3",
    fontSize: "1rem",
    fontFamily: "inherit",
  },
};
